import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../context/AuthContext";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import LootReveal from "../components/LootReveal";
import { Check, Link2, ChevronRight, Loader2, Package, Sparkles, Eye, EyeOff, Share2, RefreshCw } from "lucide-react";

const RARITY_COLOR = { common: "#22c55e", rare: "#60A5FA", epic: "#a855f7", legendary: "#FFD166" };
const RARITY_OPTIONS = ["all", "common", "rare", "epic", "legendary"];
const DELIVERY_OPTIONS = ["all", "unopened", "pending", "processing", "paid", "cancelled"];

export default function Challenges() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [stakeName, setStakeName] = useState("");
  const [confirmedPublic, setConfirmedPublic] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [betId, setBetId] = useState("");
  const [linking, setLinking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [msg, setMsg] = useState(null);
  const [bets, setBets] = useState([]);
  const [crates, setCrates] = useState([]);
  const [remaining, setRemaining] = useState(3);
  const [revealCrate, setRevealCrate] = useState(null);
  const [rarityFilter, setRarityFilter] = useState("all");
  const [deliveryFilter, setDeliveryFilter] = useState("all");

  useEffect(() => { if (!loading && !user) nav("/", { replace: true }); }, [user, loading, nav]);

  const loadData = async () => {
    try {
      const [b, c] = await Promise.all([api.get("/bets"), api.get("/crates")]);
      setBets(b.data.bets || []);
      setCrates(c.data.crates || []);
      setRemaining(c.data.remaining_today ?? 3);
    } catch {}
  };
  useEffect(() => { if (user) loadData(); }, [user]);

  const linkStake = async (e) => {
    e.preventDefault();
    setLinking(true); setMsg(null);
    try {
      const r = await api.post("/user/link-stake", { stake_username: stakeName, confirmed_public: confirmedPublic });
      if (r.data.verified) {
        setMsg({ type: "ok", text: `Linked @${r.data.stake_username} — verified on Stake.` });
      } else {
        setMsg({ type: "ok", text: `Linked @${r.data.stake_username} — unverified (${r.data.verification_note || "make sure your Stake profile is public, then re-check"}).` });
      }
      await refresh(); setStakeName(""); setConfirmedPublic(false);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    } finally { setLinking(false); }
  };

  const recheckStake = async () => {
    setVerifying(true); setMsg(null);
    try {
      const r = await api.post("/user/verify-stake");
      if (r.data.verified) {
        setMsg({ type: "ok", text: `Verified @${r.data.stake_username} on Stake.` });
      } else {
        setMsg({ type: "err", text: r.data.verification_note || "Still unverified — set your Stake profile public and try again." });
      }
      await refresh();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    } finally { setVerifying(false); }
  };

  const submitBet = async (e) => {
    e.preventDefault();
    setSubmitting(true); setMsg(null);
    try {
      const r = await api.post("/bets/submit", { bet_id: betId });
      if (r.data.pending) {
        setMsg({ type: "ok", text: r.data.message || "Bet queued for admin review." });
      } else {
        setMsg({ type: "ok", text: `Bet accepted — crate awarded` });
      }
      setBetId(""); await loadData();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    } finally { setSubmitting(false); }
  };

  const syncBets = async () => {
    if (!linked) return;
    setSyncing(true); setMsg(null); setSyncResult(null);
    try {
      const r = await api.post("/bets/sync");
      setSyncResult(r.data);
      await loadData();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Sync failed" });
    } finally { setSyncing(false); }
  };

  const filteredCrates = useMemo(() => {
    return crates.filter((c) => {
      if (rarityFilter !== "all" && c.rarity !== rarityFilter) return false;
      if (deliveryFilter === "unopened") return !c.opened;
      if (deliveryFilter !== "all" && c.delivery_status !== deliveryFilter) return false;
      return true;
    });
  }, [crates, rarityFilter, deliveryFilter]);

  const counts = useMemo(() => {
    const r = {};
    RARITY_OPTIONS.forEach((k) => { r[k] = 0; });
    const d = {};
    DELIVERY_OPTIONS.forEach((k) => { d[k] = 0; });
    r.all = crates.length; d.all = crates.length;
    crates.forEach((c) => {
      if (r[c.rarity] !== undefined) r[c.rarity]++;
      if (!c.opened) d.unopened = (d.unopened || 0) + 1;
      if (d[c.delivery_status] !== undefined) d[c.delivery_status]++;
    });
    return { r, d };
  }, [crates]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;
  const linked = !!user.stake_username;

  return (
    <AppShell>
      <SEO title="Challenges" description="Link Stake, submit 100x+ wins and open crates." path="/challenges" noindex />

      <section className="relative pt-10 md:pt-14 pb-14 px-6 md:px-10">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5 text-[12px] text-white/70">
            <Sparkles className="w-3.5 h-3.5 text-[#0A84FF]" /> Challenges
          </div>
          <h1 className="mt-6 text-[56px] md:text-[92px] font-black tracking-[-0.045em] leading-[0.92]">
            Win. <span className="bg-gradient-to-r from-[#0A84FF] to-[#a855f7] bg-clip-text text-transparent">Verify.</span> Unlock.
          </h1>
          <p className="mt-6 text-[16px] text-white/60 max-w-lg mx-auto leading-relaxed">
            Link your Stake account, submit a winning bet from a Gumble game, and every eligible bet earns you a crate.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 text-[13px] text-white/60 flex-wrap">
            <div className="glass rounded-full px-3.5 py-1.5">{remaining} / 3 crates left today</div>
            {linked && (
              <div className={`glass rounded-full px-3.5 py-1.5 inline-flex items-center gap-2 ${user.stake_verified ? "text-emerald-300" : "text-amber-300"}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: user.stake_verified ? "#22c55e" : "#fbbf24", boxShadow: `0 0 8px ${user.stake_verified ? "#22c55e" : "#fbbf24"}` }} />
                Stake: @{user.stake_username} · {user.stake_verified ? "verified" : "unverified"}
              </div>
            )}
            <PrivacyToggle user={user} onChange={refresh} />
            <ShareProfile user={user} />
          </div>
        </div>
      </section>

      <div className="px-6 md:px-10 pb-24 max-w-6xl mx-auto">
        {msg && (
          <div className={`mb-8 glass rounded-2xl p-4 text-[14px] ${msg.type === "ok" ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <section className="glass rounded-3xl p-7">
            <div className="flex items-center gap-3">
              <StepIndex n={1} done={linked} />
              <div>
                <div className="text-[16px] font-semibold">Link your Stake account</div>
                <div className="text-[12px] text-white/50 mt-0.5">Type your Stake username &middot; Set your profile public</div>
              </div>
            </div>

            {linked ? (
              <div className="mt-6 space-y-3">
                <div className={`flex items-center gap-2 text-[13px] ${user.stake_verified ? "text-emerald-300" : "text-amber-300"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: user.stake_verified ? "#22c55e" : "#fbbf24", boxShadow: `0 0 8px ${user.stake_verified ? "#22c55e" : "#fbbf24"}` }} />
                  {user.stake_verified ? `Linked & verified @${user.stake_username}` : `Linked @${user.stake_username} — unverified`}
                </div>
                {!user.stake_verified && (
                  <div className="text-[13px] text-white/60 leading-relaxed">
                    On Stake, set your profile to public (Settings &rarr; Privacy), then re-check.
                  </div>
                )}
                {!user.stake_verified && (
                  <button onClick={recheckStake} disabled={verifying}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 glass glass-hover text-[12px] text-white/80 disabled:opacity-40">
                    {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Re-check verification
                  </button>
                )}
              </div>
            ) : (
              <>
                <ol className="mt-5 text-[14px] text-white/70 space-y-2 list-decimal list-inside marker:text-white/40">
                  <li>On Stake, set your profile to public (Settings &rarr; Privacy).</li>
                  <li>Type your Stake username below and tick the confirmation.</li>
                </ol>
                <form onSubmit={linkStake} className="mt-5 space-y-3">
                  <input value={stakeName} onChange={(e) => setStakeName(e.target.value)} required placeholder="Stake username (e.g. Eddy)"
                    className="w-full glass rounded-full px-5 py-3 text-[14px] outline-none focus:border-white/30" />
                  <label className="flex items-start gap-3 text-[13px] text-white/70 cursor-pointer select-none">
                    <input type="checkbox" checked={confirmedPublic} onChange={(e) => setConfirmedPublic(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#0A84FF]" />
                    <span>My Stake profile is public</span>
                  </label>
                  <button disabled={linking || !confirmedPublic} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
                    {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Link Stake account
                  </button>
                </form>
              </>
            )}
          </section>

          <section className={`glass rounded-3xl p-7 ${!linked ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-3">
              <StepIndex n={2} disabled={!linked} />
              <div>
                <div className="text-[16px] font-semibold">Submit a winning bet</div>
                <div className="text-[12px] text-white/50 mt-0.5">Gumble games only &middot; 100x+ &middot; last 7 days</div>
              </div>
            </div>
            <form onSubmit={submitBet} className="mt-5 flex flex-col sm:flex-row gap-2">
              <input value={betId} onChange={(e) => setBetId(e.target.value)} disabled={!linked} required placeholder="Bet ID or Stake bet URL"
                className="flex-1 glass rounded-full px-5 py-3 text-[14px] outline-none focus:border-white/30 disabled:opacity-50" />
              <button disabled={!linked || submitting} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-40">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />} Submit
              </button>
            </form>
            <button type="button" onClick={syncBets} disabled={!linked || syncing}
              className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 glass glass-hover text-[12px] text-white/80 disabled:opacity-40">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Sync recent bets from Stake
            </button>

            {syncResult && (
              <SyncResultPanel result={syncResult} onClose={() => setSyncResult(null)} />
            )}
          </section>
        </div>

        {/* Crates */}
        <section className="mt-14">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[12px] uppercase tracking-[0.25em] text-white/40">Your inventory</div>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight">Crates</h2>
            </div>
            <div className="text-[13px] text-white/50">{filteredCrates.length} of {crates.length}</div>
          </div>

          {/* Filter chips */}
          <div className="mt-5 space-y-3">
            <FilterRow label="Rarity">
              {RARITY_OPTIONS.map((r) => (
                <Chip key={r} active={rarityFilter === r} onClick={() => setRarityFilter(r)}
                  color={r !== "all" ? RARITY_COLOR[r] : undefined} count={counts.r[r]}>
                  {r}
                </Chip>
              ))}
            </FilterRow>
            <FilterRow label="Delivery">
              {DELIVERY_OPTIONS.map((d) => (
                <Chip key={d} active={deliveryFilter === d} onClick={() => setDeliveryFilter(d)} count={counts.d[d]}>
                  {d}
                </Chip>
              ))}
            </FilterRow>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {filteredCrates.length === 0 && <div className="col-span-full text-white/40 text-[14px]">No crates match those filters.</div>}
            {filteredCrates.map((c) => (
              <CrateCard key={c.id} crate={c} onOpen={(opened) => { loadData(); setRevealCrate(opened); }} />
            ))}
          </div>
        </section>

        {/* Bets history */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold tracking-tight">Submitted bets</h2>
          {bets.some((b) => b.status === "pending") && (
            <p className="mt-2 text-[13px] text-amber-300">Some bets are awaiting admin review — crates arrive once approved.</p>
          )}
          <div className="mt-4 glass rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead className="text-[11px] uppercase tracking-widest text-white/40">
                  <tr><th className="text-left p-4">Bet ID</th><th className="text-left p-4">Game</th><th className="text-right p-4">Wager</th><th className="text-right p-4">Mult</th><th className="text-right p-4">Payout</th><th className="text-left p-4">Status</th></tr>
                </thead>
                <tbody>
                  {bets.length === 0 && <tr><td colSpan={6} className="p-5 text-white/40">No bets yet.</td></tr>}
                  {bets.map((b) => (
                    <tr key={b.id} className="border-t hairline">
                      <td className="p-4 text-white/70">{(b.bet_id || "").slice(0, 14)}...</td>
                      <td className="p-4">{b.game}</td>
                      <td className="p-4 text-right">${(b.wager || 0).toFixed(2)}</td>
                      <td className="p-4 text-right text-white">{(b.multiplier || 0).toFixed(1)}x</td>
                      <td className="p-4 text-right">${(b.payout || 0).toFixed(2)}</td>
                      <td className="p-4 text-emerald-300 capitalize">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {revealCrate && <LootReveal crate={revealCrate} onClose={() => setRevealCrate(null)} />}
    </AppShell>
  );
}

function StepIndex({ n, done, disabled }) {
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold ${
      done ? "bg-[#0A84FF] text-white" : disabled ? "bg-white/5 text-white/30" : "glass text-white"
    }`}>{done ? <Check className="w-4 h-4" /> : n}</div>
  );
}

function PrivacyToggle({ user, onChange }) {
  const [isPublic, setIsPublic] = useState(user.is_public !== 0);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const next = !isPublic;
      await api.patch("/user/me", { is_public: next });
      setIsPublic(next);
      onChange && onChange();
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy}
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 glass hover:bg-white/[0.06] transition-colors ${isPublic ? "text-white" : "text-white/50"}`}>
      {isPublic ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      Profile: {isPublic ? "public" : "private"}
    </button>
  );
}

function ShareProfile({ user }) {
  const [copied, setCopied] = useState(false);
  const handle = user.stake_username || user.username;
  const share = () => {
    const url = `${window.location.origin}/p/${encodeURIComponent(handle)}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={share} className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 glass hover:bg-white/[0.06]">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Share2 className="w-3.5 h-3.5" />}
      {copied ? "Link copied" : "Share profile"}
    </button>
  );
}

function SyncResultPanel({ result, onClose }) {
  const imported = result.imported || [];
  const skipped = result.skipped || [];
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[12px] uppercase tracking-widest text-white/50">
          Sync results &middot; <span className="text-emerald-300">{imported.length} imported</span> &middot; <span className="text-white/60">{skipped.length} skipped</span>
        </div>
        <button onClick={onClose} className="text-[11px] text-white/40 hover:text-white">dismiss</button>
      </div>

      {imported.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-widest text-emerald-300 mb-1">Imported</div>
          <ul className="space-y-1">
            {imported.map((b, i) => (
              <li key={"i" + i} className="flex items-center justify-between text-[13px] font-mono">
                <span className="text-white/60">{String(b.bet_id).slice(0, 22)}...</span>
                <span className="text-white">{Number(b.multiplier || 0).toFixed(1)}x <span className="text-white/50 ml-2">crate #{b.crate_id}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Skipped</div>
          <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {skipped.map((b, i) => (
              <li key={"s" + i} className="flex items-center justify-between text-[13px]">
                <span className="text-white/50 font-mono">{String(b.bet_id).slice(0, 22)}...</span>
                <span className="text-white/60">{b.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {imported.length === 0 && skipped.length === 0 && (
        <div className="mt-2 text-[13px] text-white/50">No recent bets matched. Play a Gumble game on Stake and try again.</div>
      )}
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-[11px] uppercase tracking-widest text-white/40 w-16 shrink-0">{label}</span>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, count, color }) {
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px] transition-colors capitalize border ${
        active ? "bg-white text-black border-white" : "glass glass-hover text-white/70 border-transparent"
      }`}>
      {color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}
      {children}
      {typeof count === "number" && <span className={`text-[11px] ${active ? "text-black/60" : "text-white/40"}`}>{count}</span>}
    </button>
  );
}

function CrateCard({ crate, onOpen }) {
  const [opening, setOpening] = useState(false);
  const c = RARITY_COLOR[crate.rarity] || "#e8e8ed";

  const open = async () => {
    setOpening(true);
    try {
      const r = await api.post(`/crates/${crate.id}/open`);
      onOpen(r.data.crate);
    } catch (e) {
      setOpening(false);
    }
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden group cursor-default transition-transform duration-300 hover:-translate-y-1"
      style={{
        background: `linear-gradient(180deg, ${c}18 0%, rgba(255,255,255,0.02) 55%, rgba(0,0,0,0.35) 100%)`,
        border: `1px solid ${c}55`,
        boxShadow: `0 20px 60px -30px ${c}70, inset 0 0 40px ${c}12`,
      }}
    >
      {/* top rarity bar */}
      <div className="h-1 w-full" style={{ background: c, boxShadow: `0 0 14px ${c}` }} />

      {/* rarity ribbon */}
      <div className="flex items-center justify-between px-4 pt-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: c, textShadow: `0 0 8px ${c}55` }}>
          {crate.rarity}
        </div>
        {crate.opened && (
          <div className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/10 text-white/60">
            {crate.delivery_status}
          </div>
        )}
      </div>

      {/* crate visual */}
      <div className="relative flex items-center justify-center py-6">
        {/* radial glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-32 h-32 rounded-full" style={{ background: `radial-gradient(circle, ${c}40, transparent 60%)`, filter: "blur(10px)" }} />
        </div>
        {/* pedestal ring */}
        <div className="absolute bottom-6 w-24 h-3 rounded-full opacity-60"
          style={{ background: `radial-gradient(ellipse, ${c}66, transparent 70%)` }} />
        {/* crate box */}
        <div
          className="relative w-24 h-24 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-[-6deg] group-hover:scale-105"
          style={{
            background: `linear-gradient(135deg, ${c}30, transparent 60%)`,
            border: `1px solid ${c}80`,
            boxShadow: `inset 0 0 30px ${c}30, 0 8px 30px ${c}40`,
          }}
        >
          <Package className="w-14 h-14" style={{ color: c, filter: `drop-shadow(0 0 10px ${c})` }} />
        </div>
      </div>

      {/* footer */}
      <div className="px-4 pb-4">
        {crate.opened ? (
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Stake tip</div>
            <div className="mt-1 text-3xl font-black tracking-tight" style={{ color: (crate.reward_amount || 0) > 0 ? c : "#ffffff99", textShadow: (crate.reward_amount || 0) > 0 ? `0 0 20px ${c}66` : "none" }}>
              {(crate.reward_amount || 0) > 0 ? `$${(crate.reward_amount || 0).toFixed(2)}` : "Empty"}
            </div>
          </div>
        ) : (
          <button
            onClick={open}
            disabled={opening}
            className="relative w-full py-3 rounded-xl text-[12px] font-bold uppercase tracking-[0.2em] transition-transform active:scale-[0.98] disabled:opacity-50"
            style={{
              background: `linear-gradient(180deg, ${c} 0%, ${c}cc 100%)`,
              color: "#0a0a0f",
              boxShadow: `0 6px 24px -6px ${c}, inset 0 1px 0 rgba(255,255,255,0.35)`,
            }}
          >
            {opening ? "Loading..." : "Open crate"}
          </button>
        )}
      </div>
    </div>
  );
}
